import { useCallback, useEffect, useMemo, useState, Fragment } from 'react';
import { Tab, Tabs, Tooltip, useTheme, Button } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { PageLayout } from '../../PageLayout';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { TabPanel } from '../../Atoms/TabPanel';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { ViewsModal } from '../../Compounds/CustomTableViews/ViewsModal';
import { NewViewModal } from '../../Compounds/CustomTableViews/NewViewModal';
import { Menu as CustomViewMenu } from '../../Compounds/CustomTableViews/Menu';
import {
  ApiAdvancedFilter,
  FilterableField,
  PaginatedResponse,
  getSelectedTab,
  updateState
} from '../../../store/GeneralStore';
import { ChangeHistoryRecord, fetchChangeHistory, getChangeHistorySubject } from '../../../store/ChangeHistory';
import { DefaultHistoryGridColumnDefs } from './HistoryGridColumnDefs';
import {
  CustomView,
  PrefKey,
  UserCustomizations,
  getCustomizationSubject,
  getCustomViewSubject
} from '../../../store/UserCustomizations';
import {
  buildAdvancedFilterSet,
  getColsForTab,
  getInitialState,
  mergeAndDeduplicateFilters
} from '../../Compounds/UpstackDataGrid/helpers';
import { DEFAULT_TAB_SX_STYLES } from '../../../utils/styleHelpers';
import { DetailViewCard } from '../../Compounds/CardWithTitle';

export interface TabProps {
  name: string;
  customView?: boolean;
  filter?: FilterableField[];
  tooltip?: string;
  disabledFilters?: string[];
}

export const HistoryPage = () => {
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [history, setHistory] = useState<PaginatedResponse<ChangeHistoryRecord>>();
  const [userCustomizations, setUserCustomizations] = useState<UserCustomizations>();
  const [selectedTab, setSelectedTab] = useState(getSelectedTab().history ?? 0);
  const [customViews, setCustomViews] = useState<CustomView[]>([]);
  const [customViewNames, setCustomViewNames] = useState<string[]>([]);
  const [selectedCustomView, setSelectedCustomView] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [triggerSave, setTriggerSave] = useState<boolean>(false);
  const [newlyCreatedViewName, setNewlyCreatedViewName] = useState<string | null>(null);
  const theme = useTheme();

  const filterKeys = DefaultHistoryGridColumnDefs()
    .filter((column) => column.filterable)
    .map((column) => column.field);

  const defaultTabs = useMemo(
    () =>
      [
        {
          name: 'Combined',
          filter: [],
          tooltip: 'All change history records',
          disabledFilters: []
        },
        {
          name: 'Assets',
          filter: [
            {
              field: 'recordType',
              operator: 'equals',
              value: 'asset'
            }
          ],
          tooltip: 'Change history for assets',
          disabledFilters: ['recordType']
        },
        {
          name: 'Provisional Assets',
          filter: [
            {
              field: 'recordType',
              operator: 'equals',
              value: 'provisional_asset'
            }
          ],
          tooltip: 'Change history for provisional assets',
          disabledFilters: ['recordType']
        },
        {
          name: 'Tickets',
          filter: [
            {
              field: 'recordType',
              operator: 'equals',
              value: 'ticket'
            }
          ],
          tooltip: 'Change history for tickets',
          disabledFilters: ['recordType']
        }
      ] as TabProps[],
    []
  );

  const allAvailableViews = useMemo(() => {
    const customViewsTabProps: TabProps[] = customViews
      .filter((view) => view.page === 'historyGrid_')
      .map((view) => ({ name: view.name, customView: true }));

    return [...defaultTabs, ...customViewsTabProps];
  }, [customViews, defaultTabs]);

  const handleFetchHistory = useCallback(
    async (filters?: ApiAdvancedFilter, localLoading?: boolean, forceRefresh = false) => {
      if (localLoading) setHistoryLoading(true);

      let currentTabFilters: FilterableField[] = [];
      const currentSelectedView = allAvailableViews[selectedTab];

      if (currentSelectedView) {
        currentTabFilters = (currentSelectedView.filter as FilterableField[]) || [];
      }

      await fetchChangeHistory(
        {
          ...filters,
          filters: mergeAndDeduplicateFilters(filters?.filters || [], currentTabFilters, true)
        },
        forceRefresh
      );

      if (localLoading) setHistoryLoading(false);
    },
    [allAvailableViews, selectedTab]
  );

  const handleTabChange = useCallback(
    (index: number, name: string, type: 'main' | 'custom') => {
      if (type === 'main') {
        setSelectedCustomView('');
      } else {
        setSelectedCustomView(name);
      }

      const actualIndex = allAvailableViews.findIndex((tab) => {
        if (type === 'custom') {
          return tab.name === name && tab.customView;
        }

        return tab.name === name && !tab.customView;
      });

      if (actualIndex !== -1) {
        updateState({
          selectedTab: {
            ...getSelectedTab(),
            history: actualIndex
          }
        });
        setSelectedTab(actualIndex);
      }
    },
    [allAvailableViews]
  );

  const getInitialSort = useCallback(
    (userCust: UserCustomizations) => {
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `historyGrid_${pageName}` as PrefKey;
      const savedSort = userCust?.content?.[currentPage]?.muiConfig?.sorting?.sortModel;
      return savedSort?.length ? savedSort : ([] as GridSortModel);
    },
    [allAvailableViews, selectedTab]
  );

  const getInitialFilter = useCallback(
    (userCust: UserCustomizations) => {
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `historyGrid_${pageName}` as PrefKey;
      const savedFilter = userCust?.content?.[currentPage]?.muiConfig?.filter?.filterModel;
      return savedFilter || ({} as GridFilterModel);
    },
    [allAvailableViews, selectedTab]
  );

  const dataFetch = useCallback(() => {
    if (userCustomizations && allAvailableViews[selectedTab]) {
      const initialSortModel = getInitialSort(userCustomizations);
      const initialFilterModel = getInitialFilter(userCustomizations);
      handleFetchHistory(
        {
          ...buildAdvancedFilterSet(initialFilterModel || ({} as GridFilterModel), filterKeys),
          orders: initialSortModel
            ? initialSortModel.map((sort) => ({ sort: sort.field, direction: sort.sort }))
            : undefined
        },
        true,
        true
      );
    }
  }, [
    allAvailableViews,
    filterKeys,
    getInitialFilter,
    getInitialSort,
    handleFetchHistory,
    selectedTab,
    userCustomizations
  ]);

  const handleNewViewCreated = useCallback((viewName: string) => {
    setNewlyCreatedViewName(viewName);
  }, []);

  useEffect(() => {
    const historySub = getChangeHistorySubject().subscribe((data) => setHistory(data));
    const userCustSub = getCustomizationSubject().subscribe((userCust) => setUserCustomizations(userCust));
    const customViewsSub = getCustomViewSubject().subscribe((custViews) => setCustomViews(custViews));

    return () => {
      if (historySub) historySub.unsubscribe();
      if (userCustSub) userCustSub.unsubscribe();
      if (customViewsSub) customViewsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setCustomViewNames(customViews.filter((view) => view.page === 'historyGrid_').map((view) => view.name));
  }, [customViews]);

  useEffect(() => {
    if (allAvailableViews.length > 0 && selectedTab !== undefined && selectedTab >= 0) {
      const initialSelectedView = allAvailableViews[selectedTab];
      if (initialSelectedView) {
        setSelectedCustomView(initialSelectedView.customView ? initialSelectedView.name : '');
      }
    }
  }, [allAvailableViews, selectedTab]);

  useEffect(() => {
    if (newlyCreatedViewName) {
      const newViewIndex = allAvailableViews.findIndex((tab) => tab.name === newlyCreatedViewName && tab.customView);
      if (newViewIndex !== -1) {
        handleTabChange(newViewIndex, newlyCreatedViewName, 'custom');
        setNewlyCreatedViewName(null);
      }
    }
  }, [allAvailableViews, handleTabChange, newlyCreatedViewName]);

  useEffect(() => {
    if (userCustomizations && allAvailableViews.length > 0) {
      dataFetch();
    }
  }, [allAvailableViews, selectedTab, userCustomizations]);

  useEffect(() => {
    if (selectedCustomView && !customViewNames.includes(selectedCustomView)) {
      setSelectedCustomView('');
      handleTabChange(0, defaultTabs[0].name, 'main');
    }
  }, [allAvailableViews, customViewNames, defaultTabs, handleTabChange, selectedCustomView]);

  const handleCustomViewMenuChange = useCallback(
    (name: string) => {
      const index = allAvailableViews.findIndex((tab) => tab.name === name && tab.customView);
      if (index !== -1) {
        handleTabChange(index, name, 'custom');
      }
    },
    [allAvailableViews, handleTabChange]
  );

  return (
    <PageLayout
      dataCy="history-page"
      dataTestId="history-page"
      pageTitle=""
      className="w-full mt-0">
      <DetailViewCard
        enableCollapse
        title="History"
        className="mt-2">
        <Fragment>
          <Tabs
            sx={DEFAULT_TAB_SX_STYLES}
            value={selectedTab}
            variant="scrollable"
            scrollButtons="auto"
            onChange={(_event, index) => {
              const currentTab = allAvailableViews[index];
              if (currentTab) {
                handleTabChange(index, currentTab.name, currentTab.customView ? 'custom' : 'main');
              }
            }}
            slotProps={{
              indicator: {
                sx: {
                  backgroundColor: theme.palette.primary.main,
                  height: allAvailableViews[selectedTab]?.customView ? 0 : 2,
                  borderRadius: 1
                }
              }
            }}>
            {defaultTabs.map((tab) => (
              <Tab
                sx={{ textTransform: 'none', fontSize: '.8rem', color: 'grey6' }}
                key={tab.name}
                label={
                  <Tooltip
                    title={<span style={{ whiteSpace: 'pre-line' }}>{tab.tooltip}</span>}
                    slotProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 'none',
                          width: 'auto'
                        }
                      }
                    }}>
                    <span>{tab.name}</span>
                  </Tooltip>
                }
              />
            ))}
          </Tabs>
          <div className="-mt-12 float-right inline-flex pr-4">
            <div className={`flex ${selectedCustomView ? 'border-b-2 border-indigo' : ''}`}>
              <div
                key="custom-views"
                className="self-center">
                <CustomViewMenu
                  setSelectedView={setSelectedCustomView}
                  selectedView={selectedCustomView}
                  viewOptions={customViewNames}
                  handleViewChange={handleCustomViewMenuChange}
                  defaultTitle="My Views"
                />
              </div>
            </div>
            <div className="ml-3 self-center">
              <Button
                onClick={() => setTriggerSave(true)}
                startIcon={<AddCircleIcon />}
                sx={{
                  fontSize: '.8rem',
                  textTransform: 'none'
                }}>
                Save As View
              </Button>
            </div>
            <div
              className="ml-3 self-center"
              key="add-custom-view">
              <Button
                onClick={() => setOpen(true)}
                startIcon={<EditNoteIcon />}
                sx={{
                  fontSize: '.8rem',
                  textTransform: 'none'
                }}>
                Edit Views
              </Button>
            </div>
          </div>
          {allAvailableViews.map((tab, index) => {
            let tabKeyName = tab.name || '';
            if (!tab.customView) {
              tabKeyName = tabKeyName.replace(/\s/g, '');
            }
            const tabKey = `historyGrid_${tabKeyName}` as PrefKey;
            const colsForTab = getColsForTab(DefaultHistoryGridColumnDefs(), tab.disabledFilters ?? []);
            const gridSettingsForTab = userCustomizations?.content?.[tabKey];
            let initialState;

            if (gridSettingsForTab) {
              initialState = getInitialState(gridSettingsForTab, colsForTab, tabKey);
              const initialFilterModel = getInitialFilter(userCustomizations!);
              if (initialState && initialFilterModel) {
                if (initialState.filter) {
                  initialState.filter.filterModel = initialFilterModel;
                } else {
                  initialState.filter = { filterModel: initialFilterModel };
                }
              }
            }

            return (
              <Fragment key={tab.name}>
                {(!initialState || historyLoading) && (
                  <div className="flex h-[45rem] m-auto">
                    <ComponentSpinner label="Loading data..." />
                  </div>
                )}
                {initialState && !historyLoading && userCustomizations && (
                  <TabPanel
                    key={tab.name}
                    index={index}
                    value={selectedTab}
                    className="max-w-[60vw] min-w-full">
                    <UpstackDataGrid
                      title="History"
                      rows={history?.data || []}
                      columns={colsForTab}
                      initialState={initialState}
                      page={tabKey}
                      gridSettings={gridSettingsForTab}
                      showSearch={false}
                      apiFilter
                      fetchFunction={handleFetchHistory}
                      rowCount={history?.totalCount}
                      rowPrimaryKey="uniqueKey"
                      advancedFiltering
                      filterKeys={filterKeys}
                      triggerSave={triggerSave}
                      handleSaveComplete={() => setTriggerSave(false)}
                      handleNewTabModalOpen={() => setCreateOpen(true)}
                      sxStyles={{
                        borderWidth: '0',
                        borderBottomLeftRadius: '.75rem',
                        borderBottomRightRadius: '.75rem',
                        height: 'calc(100vh - 300px)'
                      }}
                      customNoRowsMessage="No history records available"
                    />
                  </TabPanel>
                )}
              </Fragment>
            );
          })}
        </Fragment>
      </DetailViewCard>
      <ViewsModal
        open={open}
        handleClose={() => setOpen(false)}
        type="history"
      />
      <NewViewModal
        onViewCreated={handleNewViewCreated}
        open={createOpen}
        setOpen={setCreateOpen}
        sourceTab={allAvailableViews[selectedTab]?.name || defaultTabs[0].name}
        type="history"
        sourceTabFilter={allAvailableViews[selectedTab]?.filter}
      />
    </PageLayout>
  );
};
